import { useEffect } from "react";
import axios from "axios";
import { config } from "../config";

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("Unable to read partner logo"));
      }
    });
    reader.addEventListener("error", () => reject(reader.error));
    reader.readAsDataURL(blob);
  });
}

function setPartnerFavicon(dataUrl: string, mimeType: string) {
  let favicon = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
  if (!favicon) {
    favicon = document.createElement("link");
    favicon.rel = "icon";
    document.head.appendChild(favicon);
  }

  favicon.href = dataUrl;
  if (mimeType) {
    favicon.type = mimeType;
  }
}

/**
 * Sets the document favicon from the partner logo for the active link.
 * Mounted at the app root so it applies regardless of splash timing or route.
 */
export function usePartnerFavicon(linkId: string | undefined) {
  useEffect(() => {
    if (!linkId) return;

    let cancelled = false;

    const load = async () => {
      try {
        const apiBase = config.API_BASE_URL.replace(/\/$/, "");
        const headers: Record<string, string> = { "x-link-id": linkId };
        if (apiBase.includes("ngrok")) {
          headers["ngrok-skip-browser-warning"] = "true";
        }

        const response = await axios.get(
          `${apiBase}/widget/links/${linkId}/logo`,
          { responseType: "blob", headers },
        );

        if (cancelled) return;

        const logoBlob = response.data as Blob;
        const faviconDataUrl = await blobToDataUrl(logoBlob);
        if (!cancelled) {
          setPartnerFavicon(faviconDataUrl, logoBlob.type);
        }
      } catch {
        // 404 or network error: keep the default favicon from index.html.
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [linkId]);
}
